<template>
  <div class="simulation-controls column gap-2">
    <div class="simulation-controls__buttons">
      <button
        class="simulation-controls__button button"
        :disabled="nextDisabled"
        type="button"
        @click="next"
      >
        <span v-if="simulationView.simulating || simulationView.visualizing">
          {{ $t("labels.simulating") }}...
        </span>
        <span v-else-if="simulationView.currentRoundIndex == -1">
          {{ $t("actions.start") }}
        </span>
        <span v-else>
          {{ $t("actions.simulate") }}
        </span>
      </button>

      <div class="flex gap-1">
        <button
          class="button-icon simulation-controls__icon"
          :title="$t('labels.settings')"
          type="button"
          @click="settingsOpen = !settingsOpen"
        >
          <lucide-icon name="settings" />
        </button>

        <button
          class="button-icon simulation-controls__icon text-16"
          :title="$t('labels.info')"
          type="button"
          @click="openInOverlay(simulationView.simulation)"
        >
          <lucide-icon name="info" />
        </button>

        <button
          class="button-icon simulation-controls__icon"
          :title="$t('labels.download')"
          type="button"
          @click="exportSimulation()"
        >
          <lucide-icon name="download" />
        </button>

        <button
          class="button-icon simulation-controls__icon"
          type="button"
          @click="autoplayControlsVisible = !autoplayControlsVisible"
        >
          <lucide-icon name="more-horizontal" />
        </button>
      </div>
    </div>

    <transition name="simulation-controls__autoplay">
      <div
        v-show="autoplayControlsVisible"
        class="simulation-controls__autoplay"
      >
        <button
          class="simulation-controls__button button"
          :disabled="playDisabled"
          type="button"
          @click="toggleAutoplay"
        >
          <span>
            {{
              simulationView.autoplayActive
                ? $t("labels.stopAutoPlay")
                : $t("labels.startAutoPlay")
            }}
          </span>
        </button>
      </div>
    </transition>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";

import { showConfirm } from "@core/ui/alert/alert";

import LucideIcon from "@core/ui/LucideIcon.vue";

import { useSimulationViewStore } from "@editor/views/simulation/simulationView.store";
import { useOverlay } from "@editor/ui/overlayDialog.store";
import { useSelectionStore } from "@editor/store/editor/selection.store";
import { useSimulationStore } from "@editor/items/simulation/simulations.store";

import type { OverlayDialogItemRequest } from "@editor/ui/OverlayDialogItem";

const simulationView = useSimulationViewStore();
const { t } = useI18n();

const autoplayControlsVisible = ref(false);

const playDisabled = computed(() => {
  // always allow the simulation to be stopped.
  if (simulationView.autoplayActive) return false;

  if (simulationView.simulation?.complete) return true;

  return nextDisabled.value;
});

const nextDisabled = computed(() => {
  // no stepping further until the current simulation is done.
  if (simulationView.simulating) return true;
  if (simulationView.visualizing) return true;
  if (simulationView.simulation?.complete) return true;
  if (simulationView.currentRoundIndex == -1) return false;
  return false;
});

const settingsOpen = computed({
  get() {
    return simulationView.settingsOpen;
  },
  set(value: boolean) {
    simulationView.set({ settingsOpen: value });
  }
});

// ==============================
// CONTROLS:

function pause() {
  simulationView.set({ autoplayActive: false });
}

async function next() {
  if (simulationView.simulation === null) return;

  useSelectionStore().clearAll();

  // simulate round if it has not been simulated yet.
  
  // move to current round if needed (e.g. when user has jumped to a past round to inspect it).
  if (simulationView.selectedRound != null && simulationView.selectedRound.index !== simulationView.currentRoundIndex) {
    simulationView.goToRound(simulationView.currentRoundIndex);
  }

  do {
    await simulationView.simulateAndVisualizeRound();
  } while(
    // autoplay is handled here
    simulationView.autoplayActive && simulationView.simulation != null && !simulationView.simulation.complete
  );
}

// ==============================
// AUTOPLAY:

function toggleAutoplay() {
  if (simulationView.autoplayActive) pause();
  else startAutoplay();
}

async function startAutoplay() {
  if (await showConfirm(t("questions.confirmAutoplay"))) {
    simulationView.set({ autoplayActive: true });

    return next();
  }
}

// ==============================
// OTHER:

function openInOverlay(item: OverlayDialogItemRequest | null) {
  useOverlay().setItem(item);
}

function exportSimulation() {
  if (simulationView.simulation === null) {
    throw new Error("No simulation selected");
  }
  useSimulationStore().export(simulationView.simulation);
}
</script>

<style lang="scss">
.simulation-controls {
  &__buttons {
    display: flex;
    justify-content: space-between;
  }
  &__button {
    white-space: nowrap;
    text-transform: none;
  }
  &__icon {
    font-size: 15px;
    padding-left: 6px;
    padding-right: 7px;
  }
  &__autoplay {
    background-image: linear-gradient(transparent, var(--color-background));
    padding-bottom: 0.5rem;
    border-bottom-left-radius: 0.5rem;
    border-bottom-right-radius: 0.5rem;

    /* Enter and leave animations can use different */
    /* durations and timing functions.              */
    &-enter-active {
      transition: all 300ms ease;
    }
    &-leave-active {
      transition: all 200ms cubic-bezier(1, 0.5, 0.8, 1);
    }
    &-enter-from,
    &-leave-to {
      transform: translateY(-10px);
      opacity: 0;
    }
  }
}
</style>
